import {
  createRelations,
  isAfterRule,
  type Chain,
  type GraphRelations
} from './layout';
import { CONNECTOR_TIGHT_WIDTH, CONNECTOR_WIDTH, NODE_WIDTH } from './constants';
import { PlacementRuleType } from '../../../api/generated';
import type { EditorNode } from './types';

// The connector rendered before a node is the tight one when the node's primary
// "after" rule is "immediately after" (matches the Connector component), so its
// column is narrower.
const incomingConnectorWidth = (node: EditorNode): number =>
  node.dependencies.some((d) => d.type === PlacementRuleType.ImmediatelyAfter)
    ? CONNECTOR_TIGHT_WIDTH
    : CONNECTOR_WIDTH;

// The node the chain follows into its head. Usually the head carries the
// "after" rule directly; when a node is inserted at the front of a chain it has
// no predecessor of its own, so we fall back to the first "after" target a
// chain node has outside this chain (e.g. the second node still being "after"
// the node above). Returns undefined when the chain has no external entry.
export function findChainEntrySource(
  nodes: EditorNode[],
  chain: Chain,
  relations?: GraphRelations
): string | undefined {
  const { getPredId } = relations ?? createRelations(nodes);
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const chainIds = new Set(chain.items.map((item) => item.id));
  const first = chain.items[0];
  if (!first) return undefined;

  let source = getPredId(first);
  if (!source) {
    for (const item of chain.items) {
      const external = item.dependencies.find(
        (d) => isAfterRule(d.type) && d.targetId && !chainIds.has(d.targetId) && byId.has(d.targetId)
      )?.targetId;
      if (external) {
        source = external;
        break;
      }
    }
  }

  return source && !chainIds.has(source) && byId.has(source) ? source : undefined;
}

// Centre the branch between where it leaves and where it rejoins. With only
// one of the two, align to that column; with neither, it's a main row.
function centeredIndent(leaveX: number | undefined, rejoinX: number | undefined): number {
  if (leaveX !== undefined && rejoinX !== undefined) return (leaveX + rejoinX) / 2;
  if (leaveX !== undefined) return leaveX;
  if (rejoinX !== undefined) return rejoinX;
  return 0;
}

// Horizontal indent (px) for each chain row, so a branch row starts in the
// column just after the node it branches from. That keeps the wrapped node
// under its inline sibling and stops the link line from crossing back to the
// left. Chains with no external entry (main rows) get 0.
export function computeChainIndents(nodes: EditorNode[], chains: Chain[]): Map<string, number> {
  const relations = createRelations(nodes);
  const { getSuccessors } = relations;
  const position = new Map<string, { chainId: string; index: number }>();
  chains.forEach((chain) =>
    chain.items.forEach((item, index) => position.set(item.id, { chainId: chain.id, index }))
  );
  const chainById = new Map(chains.map((chain) => [chain.id, chain]));
  // Render order: a chain is only positioned relative to an earlier (more
  // primary) chain, never a later one. This keeps the main row at indent 0 and
  // avoids two chains trying to position off each other.
  const chainOrder = new Map(chains.map((chain, order) => [chain.id, order]));
  const indents = new Map<string, number>();

  const isEarlier = (otherChainId: string, chainId: string): boolean =>
    (chainOrder.get(otherChainId) ?? 0) < (chainOrder.get(chainId) ?? 0);

  // When the plan has both a FIRST and a LAST, those columns are reserved: no
  // branch row may sit under them, so branches are confined to the columns
  // strictly between the two.
  const firstNode = nodes.find((n) => n.dependencies.some((d) => d.type === PlacementRuleType.First));
  const lastNode = nodes.find((n) => n.dependencies.some((d) => d.type === PlacementRuleType.Last));

  function indentOf(chain: Chain, visiting: Set<string>): number {
    const cached = indents.get(chain.id);
    if (cached !== undefined) return cached;
    if (visiting.has(chain.id)) return 0; // guard against dependency cycles
    visiting.add(chain.id);
    indents.set(chain.id, 0); // provisional, breaks re-entrancy loops via nodeX

    const leaveX = leaveColumnX(chain, visiting);
    const rejoinX = rejoinColumnX(chain, visiting);
    let indent = centeredIndent(leaveX, rejoinX);
    if (!chainHoldsAnchor(chain)) indent = clampBetweenAnchors(indent, visiting);

    visiting.delete(chain.id);
    indents.set(chain.id, indent);
    return indent;
  }

  // Leave column: the box just after the predecessor on an earlier row, where
  // the branch leaves the flow (so a plain fork sits under that box).
  function leaveColumnX(chain: Chain, visiting: Set<string>): number | undefined {
    const source = findChainEntrySource(nodes, chain, relations);
    const sourcePos = source ? position.get(source) : undefined;
    if (!source || !sourcePos || !isEarlier(sourcePos.chainId, chain.id)) return undefined;
    const sourceChain = chainById.get(sourcePos.chainId);
    const inlineSuccessor = sourceChain?.items[sourcePos.index + 1];
    return inlineSuccessor
      ? nodeX(inlineSuccessor.id, visiting)
      : nodeX(source, visiting) + NODE_WIDTH + CONNECTOR_WIDTH;
  }

  // Rejoin column: the box just before the node the branch flows back into on
  // an earlier row.
  function rejoinColumnX(chain: Chain, visiting: Set<string>): number | undefined {
    const exit = exitInfo(chain);
    const exitPos = exit ? position.get(exit.targetId) : undefined;
    const exitNode = exit ? nodes.find((n) => n.id === exit.targetId) : undefined;
    if (!exit || !exitPos || !exitNode) return undefined;
    const exitChain = chainById.get(exitPos.chainId);
    const inlinePredecessor = exitChain?.items[exitPos.index - 1];
    return inlinePredecessor
      ? nodeX(inlinePredecessor.id, visiting)
      : Math.max(0, nodeX(exit.targetId, visiting) - NODE_WIDTH - incomingConnectorWidth(exitNode));
  }

  function chainHoldsAnchor(chain: Chain): boolean {
    const firstId = firstNode?.id;
    const lastId = lastNode?.id;
    return (
      (firstId !== undefined && chain.items.some((it) => it.id === firstId)) ||
      (lastId !== undefined && chain.items.some((it) => it.id === lastId))
    );
  }

  // Reserve the FIRST and LAST columns: any chain that doesn't itself hold an
  // anchor must sit after the FIRST column and before the LAST column, so
  // nothing lands directly under FIRST or LAST.
  function clampBetweenAnchors(indent: number, visiting: Set<string>): number {
    const lowerBound = firstNode
      ? nodeX(firstNode.id, visiting) + NODE_WIDTH + CONNECTOR_WIDTH
      : 0;
    const upperBound = lastNode
      ? nodeX(lastNode.id, visiting) - NODE_WIDTH - CONNECTOR_WIDTH
      : Number.POSITIVE_INFINITY;
    return Math.max(lowerBound, Math.min(indent, upperBound));
  }

  // Absolute x of a node: its chain's indent plus the width of every box and
  // connector before it on that row (connector width varies with rule type).
  function nodeX(nodeId: string, visiting: Set<string>): number {
    const pos = position.get(nodeId);
    const chain = pos ? chainById.get(pos.chainId) : undefined;
    if (!pos || !chain) return 0;
    let xPos = indentOf(chain, visiting);
    for (let i = 1; i <= pos.index; i++) {
      xPos += NODE_WIDTH + incomingConnectorWidth(chain.items[i]);
    }
    return xPos;
  }

  // First chain node that flows forward into another row (e.g. its head being
  // "before" a node on another row), with that successor. Used to place a chain
  // that has no predecessor of its own.
  function exitInfo(chain: Chain): { index: number; targetId: string } | undefined {
    for (let i = 0; i < chain.items.length; i++) {
      for (const successor of getSuccessors(chain.items[i].id)) {
        const sp = position.get(successor.id);
        if (sp && isEarlier(sp.chainId, chain.id)) return { index: i, targetId: successor.id };
      }
    }
    return undefined;
  }

  for (const chain of chains) indentOf(chain, new Set());
  return indents;
}
